import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import {
  Groups,
  History,
  AssignmentTurnedIn,
  AccessTime,
  CheckCircle,
  CalendarMonth,
  Quiz,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { teacherAPI } from '../../services/api';
import TeacherPageHeader from './components/TeacherPageHeader';
import TeacherStatCard from './components/TeacherStatCard';
import TeacherPanel from './components/TeacherPanel';
import { TeacherEmptyState, TeacherLoading } from './components/TeacherEmptyState';
import { gridSx, sectionTitleSx } from './components/teacherTokens';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalClasses: 0,
    completedAssignments: 0,
    pendingAssignments: 0,
    totalAssignments: 0,
    todayClasses: 0,
    weeklyHours: 0,
    allCompletedClasses: 0,
    notifications: 0,
  });
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await teacherAPI.getDashboard();
      const data = response.data;

      // Map backend stats into your frontend shape
      setStats({
        totalStudents: data.stats?.totalStudents || 0,
        totalClasses: data.stats?.monthlyClasses || 0,
        completedAssignments: data.stats?.assignmentsCompleted || 0,
        pendingAssignments: data.stats?.assignmentsPending || 0,
        totalAssignments: data.stats?.assignmentsTotal || 0,
        todayClasses: data.stats?.todayClasses || 0,
        completedToday: data.stats?.completedToday || 0,
        monthlyHours: data.stats?.monthlyHours || 0,
        weeklyHours: data.stats?.weeklyHours || 0,
        allCompletedClasses: data.stats?.allCompletedClasses || 0, // Total completed classes (all time)
        totalCompletedHours: data.stats?.totalCompletedHours || 0, // Total completed hours (all time)
        notifications: 0, // backend not providing
      });

      // Upcoming classes
      setUpcomingClasses(
        (data.upcomingClasses || []).map((cls) => ({
          _id: cls._id,
          subject: cls.subject,
          time: cls.startTime || new Date(cls.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          studentName: cls.studentId?.studentName || 'Unknown',
          studentId: cls.studentId,
          status: cls.status,
        }))
      );

      // Recent activity (derive from classes for now)
      const activities = [
        ...(data.todaysClasses || []).map((cls) => ({
          _id: cls._id,
          type: 'class',
          title: `${cls.status === 'completed' ? 'Completed' : 'Scheduled'} ${cls.subject} Class`,
          description: `Class with ${cls.studentId?.studentName || 'Unknown'}`,
          timestamp: cls.scheduledDate,
        })),
        ...(data.upcomingClasses || []).slice(0, 2).map((cls) => ({
          _id: cls._id,
          type: 'upcoming',
          title: `Upcoming ${cls.subject} Class`,
          description: `Scheduled with ${cls.studentId?.studentName || 'Unknown'}`,
          timestamp: cls.scheduledDate,
        }))
      ];

      setRecentActivity(activities.slice(0, 5));

    } catch (error) {
      console.error('Dashboard fetch error:', error);
      setError('Failed to load dashboard data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const teacherName = user?.profile?.name || user?.name || 'Teacher';

  // `.ov-row` — label left, bold figure right.
  const OverviewRow = ({ label, value }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '11px 2px',
        borderBottom: '1px solid',
        borderColor: (t) => t.palette.teacher.line2,
        fontSize: '14.5px',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <span>{label}</span>
      <Box component="b" sx={{ fontWeight: 800, fontSize: '16px' }}>
        {value}
      </Box>
    </Box>
  );

  if (loading) {
    return <TeacherLoading label="Loading your dashboard…" />;
  }

  return (
    <Box>
      <TeacherPageHeader
        variant="welcome"
        title={`Welcome back, ${teacherName}!`}
        subtitle="Here's an overview of your teaching activities"
      />

      {error && (
        <Alert severity="error" sx={{ mb: '18px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* `.grid.g3` */}
      <Box sx={{ ...gridSx(3), mb: '16px' }}>
        <TeacherStatCard
          icon={<Groups />}
          value={stats.totalStudents}
          label="My Students"
          subtitle="Students assigned to you"
        />
        <TeacherStatCard
          icon={<History />}
          value={stats.totalClasses}
          label="Total Classes (Monthly)"
          subtitle="This month"
        />
        <TeacherStatCard
          icon={<AssignmentTurnedIn />}
          value={`${stats.completedAssignments}/${stats.totalAssignments}`}
          label="Assignments"
          subtitle="Completed / Total (Homework + Quick Tests)"
        />
      </Box>

      {/* `.grid.g2` */}
      <Box sx={{ ...gridSx(2), mb: '8px' }}>
        <TeacherStatCard
          icon={<AccessTime />}
          value={`${stats.monthlyHours || 0}h`}
          label="Total Class Monthly Hours"
          subtitle="Total hours this month"
        />
        <TeacherStatCard
          icon={<CheckCircle />}
          tone="ok"
          value={`${stats.totalCompletedHours || 0}h`}
          label="Completed Hours"
          subtitle="Total completed hours"
        />
      </Box>

      {/* Today's Overview · Upcoming Classes · Quick Actions */}
      <Box sx={{ ...gridSx(3), mt: '24px', alignItems: 'stretch' }}>
        <TeacherPanel title="Today's Overview" sx={{ display: 'flex', flexDirection: 'column' }}>
          <OverviewRow label="Classes Today" value={stats.todayClasses || 0} />
          <OverviewRow label="Completed Today" value={stats.completedToday || 0} />
          <OverviewRow
            label="Remaining Today"
            value={Math.max(0, (stats.todayClasses || 0) - (stats.completedToday || 0))}
          />
          <OverviewRow label="Weekly Hours" value={`${stats.weeklyHours || 0}h`} />
          <Box sx={{ padding: '14px 0 6px' }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => navigate('/teacher/calendar')}
            >
              View Schedule
            </Button>
          </Box>
        </TeacherPanel>

        <TeacherPanel title="Upcoming Classes" sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1 }}>
            {upcomingClasses.length === 0 ? (
              <TeacherEmptyState dense icon={<CalendarMonth />} title="No upcoming classes" />
            ) : (
              upcomingClasses.slice(0, 3).map((cls) => (
                <Box
                  key={cls._id}
                  sx={{
                    padding: '14px 2px',
                    borderBottom: '1px solid',
                    borderColor: (t) => t.palette.teacher.line2,
                    '&:last-of-type': { borderBottom: 'none' },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '14.5px' }}>{cls.subject}</Typography>
                  <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mt: '2px' }}>
                    {cls.time} · {cls.studentName}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
          <Box sx={{ padding: '0 0 12px' }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => navigate('/teacher/classes')}
            >
              View All Classes
            </Button>
          </Box>
        </TeacherPanel>

        <TeacherPanel title="Quick Actions">
          {/* `.qa` */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', py: '6px' }}>
            <Button variant="contained" fullWidth onClick={() => navigate('/teacher/homework')}>
              Create Homework
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<Quiz />}
              onClick={() => navigate('/teacher/quick-test?create=true')}
            >
              Create Quick Test
            </Button>
            <Button variant="outlined" fullWidth onClick={() => navigate('/teacher/students')}>
              View Students
            </Button>
            <Button variant="outlined" fullWidth onClick={() => navigate('/teacher/reports')}>
              Generate Report
            </Button>
          </Box>
        </TeacherPanel>
      </Box>

      {/* `.section-t` + recent activity panel */}
      <Typography sx={sectionTitleSx}>Recent Activity</Typography>
      <TeacherPanel>
        {recentActivity.length === 0 ? (
          <TeacherEmptyState dense icon={<History />} title="No recent activity" />
        ) : (
          recentActivity.slice(0, 3).map((activity, index) => (
            // `.ra`
            <Box
              key={activity._id || index}
              sx={{
                padding: '14px 2px',
                borderBottom: '1px solid',
                borderColor: (t) => t.palette.teacher.line2,
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '14.5px' }}>
                {activity.title || 'Activity'}
              </Typography>
              <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mt: '2px' }}>
                {activity.description || 'No description'}
              </Typography>
            </Box>
          ))
        )}
      </TeacherPanel>
    </Box>
  );
};

export default Dashboard;
